import { useRef } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useReducedMotion,
} from "framer-motion";

const PHASES = [
    {
        n: "01",
        title: "Foundation & Market Development",
        body: "Establishing the operational base, partnerships and supply chain ecosystem from Malaysia.",
    },
    {
        n: "02",
        title: "ASEAN Expansion",
        body: "Scaling commercial relationships and distribution across Singapore, Indonesia, Thailand, Vietnam and the Philippines.",
    },
    {
        n: "03",
        title: "Middle East Growth",
        body: "Activating halal-focused growth across UAE, Saudi Arabia, Qatar, Oman, Bahrain, Turkey, Iran and Lebanon.",
    },
    {
        n: "04",
        title: "Oceania Market Entry",
        body: "Building distribution partnerships and FMCG channels across Australia and New Zealand.",
    },
    {
        n: "05",
        title: "Global Consolidation",
        body: "Consolidating presence with strategic North American entry and a fully integrated cross-continental trade network.",
    },
];

export const Vision = () => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start center", "end center"],
    });
    const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

    return (
        <section
            ref={ref}
            id="vision"
            data-testid="vision-section"
            className="relative bg-[#0B0807]/82 backdrop-blur-md py-24 sm:py-32 lg:py-40 px-4 sm:px-8 lg:px-12 border-t border-white/5"
        >
            <div className="flex items-center gap-3 font-mono-text text-[10px] sm:text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">
                <span className="h-px w-10 bg-[#D69331]" />
                <span>08 / Five-Year Strategic Vision</span>
            </div>

            <motion.h2
                initial={{ y: 60, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="font-display uppercase font-bold leading-[0.9] tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl max-w-5xl"
            >
                Driving sustainable growth{" "}
                <span className="text-zinc-600">
                    through global trade excellence.
                </span>
            </motion.h2>

            <div className="mt-20 relative grid grid-cols-1 md:grid-cols-12 gap-y-2">
                {/* Track line */}
                <div className="hidden md:block md:col-span-1 relative">
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/10 -translate-x-1/2" />
                    <motion.div
                        style={reduce ? { height: "100%" } : { height: lineHeight }}
                        className="absolute left-1/2 top-0 w-px bg-[#D69331] -translate-x-1/2 origin-top"
                    />
                </div>

                <div className="md:col-span-11 space-y-px">
                    {PHASES.map((p, i) => (
                        <motion.div
                            key={p.n}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.6, delay: i * 0.05 }}
                            className="grid grid-cols-12 gap-4 items-start py-8 sm:py-10 border-t border-white/10 group hover:bg-[#0a0a0a] transition-colors px-2"
                        >
                            <span className="col-span-3 sm:col-span-2 font-mono-text text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                                Phase {p.n}
                            </span>
                            <h3 className="col-span-9 sm:col-span-5 font-display uppercase text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight text-white group-hover:text-[#D69331] transition-colors">
                                {p.title}
                            </h3>
                            <p className="col-span-12 sm:col-span-5 font-mono-text text-sm text-zinc-400 leading-relaxed">
                                {p.body}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
